/**
 * Blog post: TELC B2 Schreiben Guide with structure, scoring criteria,
 * Redemittel and a full model letter (Beschwerdebrief).
 * SEO target: "telc b2 schreiben", "telc b2 schreiben questions pdf",
 * "telc b2 writing guide", "beschwerdebrief b2" (GSC striking-distance
 * cluster around the ranking Redemittel post).
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function TelcB2SchreibenGuidePost() {
  const lang = useLangParam();

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>By the Logami team. Built by German teachers, checked against the official telc scoring criteria.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        The <strong>telc B2 Schreiben</strong> section gives you exactly 30 minutes to produce a formal
        letter that examiners score in minutes. Most candidates fail it for the same reason: they treat it
        as a language test, when it is really a <strong>structure test</strong>. This guide walks through the
        task format, the official scoring criteria, a paragraph-by-paragraph template, and a complete model
        letter you can adapt in the exam.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The task format: what actually happens in those 30 minutes
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        You receive <strong>two writing tasks and choose one</strong>. In almost every exam both are formal
        letters in Sie-form: typically a <strong>Beschwerdebrief</strong> (letter of complaint) or a formal
        request for information. Each task lists <strong>four guiding points (Leitpunkte)</strong>, and you
        need to address <strong>at least three of them</strong> properly. Aim for roughly
        <strong> 150 to 200 words</strong>: long enough to develop three points, short enough to control
        your error rate.
      </p>
      <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] mb-10">
        <h3 className="font-space text-lg font-bold text-black mb-3">Quick facts</h3>
        <ul className="list-disc pl-5 text-base text-black/70 leading-relaxed space-y-2">
          <li>30 minutes, one formal letter, Sie-form</li>
          <li>Choose 1 of 2 tasks; address at least 3 of 4 Leitpunkte</li>
          <li>No addresses or date needed; start directly with the Betreff (subject line)</li>
          <li>Scored on content, communicative design, and formal accuracy</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How examiners score your letter
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        telc raters grade the Schreiben task on three criteria. Knowing them changes how you write:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-10">
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-2">1. Content</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Did you actually deal with the Leitpunkte? Three points developed in two or three sentences each
            beat four points mentioned in passing.
          </p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-2">2. Communicative design</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Register, structure, and connectors. A formal letter needs a Betreff, a correct Anrede, logically
            connected paragraphs, and a closing formula.
          </p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-2">3. Accuracy</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Grammar and spelling. B2 raters expect Nebensätze, Passiv, and Konjunktiv II, used correctly
            rather than everywhere.
          </p>
        </div>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The five-paragraph template that always works
      </h2>
      <ol className="list-decimal pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Betreff:</strong> one factual line. <em>Beschwerde über die Lieferung vom 12. Mai</em></li>
        <li><strong>Anrede:</strong> <em>Sehr geehrte Damen und Herren,</em> or with a name if the task provides one</li>
        <li><strong>Einleitung (1 to 2 sentences):</strong> why you are writing, with the key facts (what, when)</li>
        <li><strong>Hauptteil (3 short paragraphs):</strong> one Leitpunkt per paragraph, each opened with a connector or Redemittel</li>
        <li><strong>Schluss + Grußformel:</strong> a clear demand or request with a deadline, then <em>Mit freundlichen Grüßen</em> and your name</li>
      </ol>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Model letter: Beschwerdebrief (task type seen in real exams)
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Task: you ordered a laptop online, it arrived late and damaged, and customer service has not
        responded. Complain and demand a solution.
      </p>
      <div className="p-5 sm:p-6 rounded-2xl border-2 border-black bg-white mb-6 font-medium text-black/80 leading-relaxed text-base">
        <p className="mb-4"><strong>Betreff: Beschwerde über meine Bestellung Nr. 48213</strong></p>
        <p className="mb-4">Sehr geehrte Damen und Herren,</p>
        <p className="mb-4">
          am 3. Juli habe ich in Ihrem Onlineshop einen Laptop bestellt, der laut Bestellbestätigung
          innerhalb von fünf Werktagen geliefert werden sollte. Leider muss ich mich nun aus mehreren
          Gründen bei Ihnen beschweren.
        </p>
        <p className="mb-4">
          Zunächst einmal kam das Gerät erst nach drei Wochen bei mir an, obwohl ich es dringend für
          meine Arbeit benötigt hätte. Darüber hinaus musste ich beim Auspacken feststellen, dass das
          Display beschädigt ist, sodass sich der Laptop nicht verwenden lässt. Besonders ärgerlich finde
          ich jedoch, dass Ihr Kundenservice auf meine beiden E-Mails bis heute nicht reagiert hat.
        </p>
        <p className="mb-4">
          Ich fordere Sie daher auf, mir bis zum 15. August ein einwandfreies Ersatzgerät zu liefern oder
          den vollen Kaufpreis zu erstatten. Andernfalls sehe ich mich gezwungen, rechtliche Schritte
          einzuleiten.
        </p>
        <p className="mb-1">Mit freundlichen Grüßen</p>
        <p>Anna Petrova</p>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Notice what this letter does: three Leitpunkte in three paragraphs, each opened with a connector
        (<em>zunächst einmal, darüber hinaus, besonders ärgerlich finde ich</em>), B2 grammar where it counts
        (<em>hätte benötigt, sodass, sehe ich mich gezwungen</em>), and a concrete demand with a deadline.
        That is the full checklist an examiner needs to give high marks.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The mistakes that cost the most points
      </h2>
      <ul className="list-disc pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Missing Betreff or Grußformel.</strong> Immediate deductions in communicative design.</li>
        <li><strong>Wrong register.</strong> One du-form in a formal letter signals the wrong level.</li>
        <li><strong>Writing 300 words.</strong> More text means more errors and no extra content points.</li>
        <li><strong>Retelling the task.</strong> Develop your points instead of paraphrasing the prompt.</li>
        <li><strong>No time for corrections.</strong> Plan 5 minutes at the end to check verb endings, cases, and commas.</li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How to train this in the next two weeks
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Write one letter every second day under exam conditions: 30 minutes, no dictionary. Use the
        {' '}<Link to={`/${lang}/blog/telc-b2-redemittel-schreiben`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">telc B2 Redemittel collection</Link>{' '}
        until the phrases come automatically, then get every practice letter scored. In the
        {' '}<Link to={`/${lang}/mock-exams/telc-b2`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">Logami telc B2 mock exam</Link>{' '}
        your letter is evaluated against the same three criteria described above, so you see your content,
        structure, and accuracy scores separately after every attempt.
      </p>

      <RelatedResources provider="telc" level="b2" variant="exam-guide" />
    </article>
  );
}
